import React, { useEffect, useState } from "react";
import styled from "styled-components";

import { CustomerBreakoutColors } from "../../../../globalStyles";
import {
    IGPSData,
    IGPSProfile,
    IProfileRecord,
    ISegment,
    IStoreScoreRecord,
} from "../../../../Models/Models";
import {
    determinePropensityText,
    getPrettyNumber,
} from "../../../Common/SharedActions";

export interface IGPSPsychProfileShare {
  segments: ISegment[];
  topSegs: IProfileRecord[];
  polygons: string[];
  benchmarkProfileData: IProfileRecord[];
}

const BreakoutContainer = styled("div")`
  border: 1px solid #e4e4e4;
  display: flex;
  justify-content: space-around;
  align-items: center;
  padding: 18px 21px;
`;

const BreakoutItemContainer = styled("div")`
  display: flex;
  flex-direction: column;
  text-align: center;
  align-items: center;
`;

const TableWrapper = styled.div`
  display: grid;
  height: 100%;
  grid-template-rows: 35px auto;
  grid-template-columns: 325px auto;
`;

const CircleContainer = styled("div")`
  display: flex;
  flex-direction: column;
  align-items: center;
  white-space: nowrap;
`;

const Circle = styled("div")`
  padding: 10px;
  font-weight: bold;
  margin-bottom: 3px;
  height: 48px;
  width: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Img = styled("img")`
  width: 58px;
  height: 58px;
  display: inline-block;
  border-radius: 50%;
  object-fit: cover;
  object-position: center;
  margin-right: 11px;
`;

const Header = styled.div`
  margin-bottom: 9px;
  font-size: 20px;
  font-weight: bold;
  color: #000000;
`;

interface IDataContainer {
  gridRowStart?: number;
  gridColumnStart?: number;
  gridColumnEnd?: number;
}

const DataContainer = styled.div`
  grid-row-start: ${(props: IDataContainer) => props.gridRowStart};
  grid-column-start: ${(props: IDataContainer) => props.gridColumnStart};
  grid-column-end: ${(props: IDataContainer) => props.gridColumnEnd};
  display: flex;
`;

const BreakoutText = styled.div`
  display: flex;
  margin-bottom: 5px;
  font-size: 12px;
  font-weight: bolder;
`;

const BreakoutHighlight = styled.div`
  height: 12px;
  width: 12px;
  border-radius: 50%;
  margin-right: 4px;
  background-color: ${(props: { breakoutColor: string }) =>
        props.breakoutColor};
`;

const SegmentValueText = (props) => {
    return (
        <div
            style={{
                textAlign: "center",
                display: "flex",
                flexDirection: "column",
                height: "62px",
                justifyContent: "center",
            }}
        >
            <div style={{ fontSize: 11, fontWeight: "bold" }}>
                {getPrettyNumber(props.profile.pct * 100, 2)}%
            </div>
            <div style={{ fontSize: 11 }}>of Visitors</div>
        </div>
    );
};

const GPSPsychProfileShare = ({
    segments,
    topSegs,
    polygons,
    benchmarkProfileData,
}: IGPSPsychProfileShare): JSX.Element => {
    const [storeProfileData, setStoreProfileData] = useState<IProfileRecord[]>(
        []
    );
    const [benchmarkMap, setBenchmarkMap] = useState<Map<string, IProfileRecord>>(
        new Map()
    );

    useEffect(() => {
        const baseSegments = topSegs.map((s) => s.seg);

        if (benchmarkProfileData?.length > 0) {
            const profileTwo = benchmarkProfileData
                .filter((s) => baseSegments.indexOf(s.seg) > -1)
                .slice(0, 5);

            const bMap = new Map<string, IProfileRecord>();
            for (const seg of profileTwo) {
                bMap.set(seg.seg, seg);
            }

            setBenchmarkMap(bMap);
        }

        setStoreProfileData(topSegs);
    }, []);

    const getSegment = (key: string): ISegment => {
        return segments.find((seg: ISegment) => {
            return seg.segmentKey === key;
        });
    };

    return (
        <div
            style={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
                justifyContent: "space-between",
            }}
            data-testid="gps-psych-profile-share"
        >
            <Header>
        Visitors are identified by combining lifestyle, lifestage, behavioral,
        and consumer characteristics data with mobile device data observations
        of actual customers at the selected locations.
            </Header>
            <div style={{ fontSize: 15, marginBottom: "9px", fontWeight: "bold" }}>
        Visitor Propensity
            </div>
            <BreakoutContainer>
                <BreakoutItemContainer>
                    <BreakoutText>
                        <BreakoutHighlight breakoutColor={CustomerBreakoutColors.P} />
            Primary Visitor
                    </BreakoutText>
                    <div style={{ fontSize: 11 }}>
            Households 20+% more likely to visit than the average household
                    </div>
                </BreakoutItemContainer>
                <BreakoutItemContainer>
                    <BreakoutText>
                        <BreakoutHighlight breakoutColor={CustomerBreakoutColors.S} />
            Secondary Visitor
                    </BreakoutText>
                    <div style={{ fontSize: 11 }}>
            Households up to 19% more likely to visit than the average household
                    </div>
                </BreakoutItemContainer>
                <BreakoutItemContainer>
                    <BreakoutText>
                        <BreakoutHighlight breakoutColor={CustomerBreakoutColors.N} />
            Incidental Visitor
                    </BreakoutText>
                    <div style={{ fontSize: 11 }}>
            Households less likely to visit than the average household
                    </div>
                </BreakoutItemContainer>
            </BreakoutContainer>
            <TableWrapper>
                <div style={{ fontSize: 15, alignSelf: "center", fontWeight: "bold" }}>
          Top 5 Psychographic Profiles
                </div>
                <div
                    style={{
                        fontSize: 11,
                        alignSelf: "center",
                        fontWeight: "bold",
                        textAlign: "center",
                    }}
                >
                    {polygons[0]}
                </div>
                <div
                    style={{
                        fontSize: 11,
                        gridColumnStart: 3,
                        alignSelf: "center",
                        textAlign: "center",
                    }}
                >
          Visitor Propensity
                </div>

                {benchmarkMap.size > 0 && (
                    <>
                        <div
                            data-testid="gps-psych-profile-share-benchmark-polygon-header"
                            style={{
                                fontSize: 11,
                                gridColumnStart: 4,
                                alignSelf: "center",
                                fontWeight: "bold",
                                textAlign: "center",
                            }}
                        >
                            {polygons[1]}
                        </div>
                        <div
                            data-testid="gps-psych-profile-share-benchmark-propensity-header"
                            style={{ fontSize: 11, gridColumnStart: 5, alignSelf: "center" }}
                        >
              Visitor Propensity
                        </div>
                    </>
                )}
                {storeProfileData.length > 0
          && storeProfileData.map((p, i) => {
              const segment = getSegment(p.seg);
              const benchmark = benchmarkMap.get(p.seg);

              return (
                  <React.Fragment key={`segment-description-container-${p.seg}`}>
                      <DataContainer
                          key={`${p.seg}-description`}
                          gridRowStart={i + 2}
                          style={{
                              borderLeft: "solid 1px #e4e4e4",
                              borderBottom: "solid 1px #e4e4e4",
                              borderTop: "solid 1px #e4e4e4",
                              padding: "7px 37px 6px 18px",
                          }}
                      >
                          <Img src={`/Images/segmentation-imgs/${p.seg}.jpg`} />
                          <div>
                              <div
                                  style={{
                                      fontSize: 11,
                                      fontWeight: "bold",
                                      marginBottom: "3px",
                                  }}
                              >
                                  {segment.segmentKey} {segment.name}
                              </div>
                              <div style={{ fontSize: 11 }}>{segment.description}</div>
                          </div>
                      </DataContainer>
                      <DataContainer
                          key={`${polygons[0]}-${p.seg}-info`}
                          gridRowStart={i + 2}
                          gridColumnStart={2}
                          gridColumnEnd={4}
                          style={{
                              justifyContent: "space-between",
                              padding:
                      storeProfileData.length > 1
                          ? "7px 20px 6px 42px"
                          : "7px 104px 6px 93px",
                              borderBottom: "solid 1px #e4e4e4",
                              borderTop: "solid 1px #e4e4e4",
                              borderRight: "solid 1px #e4e4e4",
                          }}
                      >
                          <SegmentValueText profile={p} />
                          <CircleContainer>
                              <Circle
                                  style={{
                                      border: `4px solid ${CustomerBreakoutColors[p.core]}`,
                                  }}
                              >
                                  {getPrettyNumber(p.index, 1)}X
                              </Circle>
                              <div style={{ fontSize: "10px" }}>
                                  <b>{determinePropensityText(p.core).toUpperCase()}</b>
                              </div>
                          </CircleContainer>
                      </DataContainer>
                      {benchmark && (
                          <DataContainer
                              key={`${polygons[1]}-${benchmark.seg}-info`}
                              gridRowStart={i + 2}
                              gridColumnStart={4}
                              gridColumnEnd={6}
                              style={{
                                  justifyContent: "space-between",
                                  padding: "7px 20px 6px 42px",
                                  borderBottom: "solid 1px #e4e4e4",
                                  borderTop: "solid 1px #e4e4e4",
                                  borderRight: "solid 1px #e4e4e4",
                              }}
                          >
                              <SegmentValueText profile={benchmark} />
                              <CircleContainer>
                                  <Circle
                                      style={{
                                          border: `4px solid ${
                                              CustomerBreakoutColors[benchmark.core]
                                          }`,
                                      }}
                                  >
                                      {getPrettyNumber(benchmark.index, 1)}X
                                  </Circle>
                                  <div style={{ fontSize: 10 }}>
                                      <b>
                                          {determinePropensityText(
                                              benchmark.core
                                          ).toUpperCase()}
                                      </b>
                                  </div>
                              </CircleContainer>
                          </DataContainer>
                      )}
                  </React.Fragment>
              );
          })}
            </TableWrapper>
        </div>
    );
};

export default GPSPsychProfileShare;
